"use client";

import { FeedEvents, trackEvent } from "@/analytics";
import { useEffect, useRef, useState } from "react";

import Link from "next/link";
import { PunchupButton } from "@punchup/ui-common/punchup/primitives/buttons/PunchupButton";
import RailModule from "./RailModule";
import { ShowWithComedian } from "@/data/shows";
import { SentryFeatureTags, logException } from "@/analytics/sentry";
import dayjs from "@punchup/utils/dayjs";
import { getApiClient } from "@punchup/utils";
import { getIpData, lookupZipcodeData } from "@/data/zipcode";
import { useMediaQuery } from "@/components/common/hooks/useMediaQuery";

const SEARCH_RADIUS_MILES = 50;
const SEARCH_DAYS_LIMIT = 60;
const MAX_ROWS = 3;

// Mirrors the placeholder filter in UpcomingShowsFeedUnitV2 — lineup-TBA rows
// don't sell a first-time visitor.
const PLACEHOLDER_COMEDIAN_NAMES = [
  "Lineup To Be Announced Soon",
  "Lineup Coming Soon",
  "Secret Lineup",
  "TBA",
  "To Be Announced",
] as const;

/**
 * "Shows near [city]" — the ticketing module of the feed's desktop rail.
 * Location comes from the visitor's IP (same path as the in-feed nearby unit);
 * renders nothing while loading or when no nearby shows are found.
 */
export default function RailShowsNearYou() {
  const [shows, setShows] = useState<ShowWithComedian[]>([]);
  const [city, setCity] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  // The rail aside only displays at xl+ (`hidden xl:block`) — fetch nothing
  // while the module can't be seen.
  const isXl = useMediaQuery("(min-width: 1280px)");
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!isXl || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    let cancelled = false;

    const fetchShows = async () => {
      try {
        const ipData = await getIpData();
        if (!ipData?.postalCode || !ipData?.countryCode) return;

        const [locationData, fetched] = await Promise.all([
          lookupZipcodeData(ipData.postalCode, ipData.countryCode),
          getApiClient().get<ShowWithComedian[]>(
            `/api/shows?postalCode=${ipData.postalCode}&countryCode=${ipData.countryCode}&radius=${SEARCH_RADIUS_MILES}&limitDays=${SEARCH_DAYS_LIMIT}`
          ),
        ]);
        if (cancelled) return;

        const upcoming = fetched.filter(
          (show) =>
            show.comedian?.display_name &&
            !(PLACEHOLDER_COMEDIAN_NAMES as readonly string[]).includes(
              show.comedian.display_name
            )
        );
        setShows(upcoming.slice(0, MAX_ROWS));
        setCity(locationData?.city ?? null);
      } catch (error) {
        logException(error, {
          extra: { data: "[feed] rail shows-near-you fetch failed" },
          tags: { feature: SentryFeatureTags.Feed },
        });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    fetchShows();
    return () => {
      cancelled = true;
    };
  }, [isXl]);

  if (!loaded || shows.length === 0) return null;

  return (
    <RailModule
      title={city ? `Shows near ${city}` : "Shows near you"}
      seeAllHref="/nearby"
      onSeeAllClick={() =>
        trackEvent(FeedEvents.CLICKED_FEED_RAIL_ITEM, {
          module: "shows-near-you",
          target: "see-all",
        })
      }
    >
      {shows.map((show) => (
        <div
          key={show.id}
          className="flex items-center justify-between gap-2 min-w-0"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium text-[var(--punchup-color-ink-1000)] truncate">
              {show.comedian?.display_name}
            </p>
            <p className="text-xs text-[var(--punchup-color-ink-500)]">
              {dayjs(show.datetime).format("ddd, MMM D · h:mm A")}
            </p>
          </div>
          <PunchupButton size="sm" asChild className="shrink-0">
            <Link
              href={`/show/${show.id}`}
              onClick={() =>
                trackEvent(FeedEvents.CLICKED_FEED_RAIL_ITEM, {
                  module: "shows-near-you",
                  target: "tickets",
                  showId: show.id,
                })
              }
            >
              Tickets
            </Link>
          </PunchupButton>
        </div>
      ))}
    </RailModule>
  );
}
